import React from "react";
import AppShell from "@/components/AppShell";

/**
 * /design/tokens — Internal visual reference for the app's typography
 * copy scale. Renders a swatch card for each of the ten approved
 * text tokens (five light-surface, five on-dark) with the hex value,
 * CSS variable / utility class name, and an example paragraph so
 * contributors can eyeball contrast at a glance and know exactly
 * which token to reach for.
 */
const LIGHT_TOKENS = [
  { name: "Body / primary",  hex: "#1A1A1A", cls: "text-foreground",       util: ".text-body",      role: "Default body copy — every paragraph, list item, and label" },
  { name: "Headline",        hex: "#111111", cls: "text-heading (auto h1–h4)", util: ".text-heading", role: "Page titles, section headings, card titles" },
  { name: "Secondary copy",  hex: "#5F6368", cls: "text-muted-foreground", util: ".text-secondary", role: "Descriptions, subtitles, breadcrumbs" },
  { name: "Helper / hint",   hex: "#757575", cls: "—",                    util: ".text-helper",    role: "Form-field hints, timestamps, metadata" },
  { name: "Disabled",        hex: "#A0A0A0", cls: "—",                    util: ".text-disabled",  role: "Disabled button labels, inactive filter chips" },
];
const DARK_TOKENS = [
  { name: "Body / primary",  hex: "#F2F2F2", cls: "—",                    util: ".text-body",      role: "Default sidebar item labels + hero-card body" },
  { name: "Headline",        hex: "#FFFFFF", cls: "aside h1–h4 (auto)",   util: ".text-heading",   role: "Sidebar section brand, hero-card titles" },
  { name: "Secondary copy",  hex: "#B8B8B8", cls: "—",                    util: ".text-secondary", role: "Hero-card subtitles, sidebar meta" },
  { name: "Muted / helper",  hex: "#8A8A8A", cls: "—",                    util: ".text-helper",    role: "Timestamps + subtle hints on dark surfaces" },
  { name: "Disabled",        hex: "#666666", cls: "—",                    util: ".text-disabled",  role: "Disabled state on dark surfaces" },
];

function TokenRow({ token, dark }) {
  return (
    <div
      className={
        "grid grid-cols-[100px_1fr] gap-4 rounded-xl border p-4 " +
        (dark ? "bg-neutral-900 border-neutral-800 text-white" : "bg-white border-neutral-200")
      }
      data-testid={`token-row-${token.hex}`}
    >
      <div
        className="h-24 rounded-lg border border-black/10"
        style={{ backgroundColor: token.hex }}
        aria-label={`Color swatch ${token.hex}`}
      />
      <div>
        <div className="flex flex-wrap items-baseline gap-3 mb-2">
          <p className={"text-heading text-lg font-semibold " + (dark ? "!text-white" : "")}>{token.name}</p>
          <code className={"text-xs font-mono " + (dark ? "text-white/70" : "text-neutral-500")}>{token.hex}</code>
        </div>
        <p className={"text-sm mb-1 " + (dark ? "text-white/80" : "text-neutral-700")}>
          {token.role}
        </p>
        <p className={"text-xs " + (dark ? "text-white/60" : "text-neutral-500")}>
          Tailwind:&nbsp;<code className={dark ? "text-white/80" : "text-neutral-700"}>{token.cls}</code>
          &nbsp;·&nbsp;Utility:&nbsp;<code className={dark ? "text-white/80" : "text-neutral-700"}>{token.util}</code>
        </p>
        <p
          className={"mt-3 text-[15px] leading-relaxed "}
          style={{ color: token.hex }}
        >
          The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs.
        </p>
      </div>
    </div>
  );
}

export default function DesignTokensPage() {
  return (
    <AppShell title="Design tokens">
      <div className="w-full py-8 space-y-10">
        <header>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
            Internal reference
          </p>
          <h1 className="text-3xl font-bold tracking-tight mt-2">Typography copy scale</h1>
          <p className="text-secondary mt-2 max-w-2xl">
            Ten tokens power every piece of text in the app — five for light
            surfaces (default), five for dark surfaces (sidebar, hero cards,
            <code className="mx-1 rounded bg-neutral-100 px-1 py-0.5 text-xs">.on-dark</code>).
            The same utility class name automatically remaps to the correct
            palette based on its parent surface, so contributors never guess.
          </p>
        </header>

        <section>
          <h2 className="text-xl font-semibold mb-3">Light surfaces</h2>
          <div className="grid gap-3">
            {LIGHT_TOKENS.map((t) => <TokenRow key={t.hex} token={t} />)}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Dark surfaces</h2>
          <div className="grid gap-3">
            {DARK_TOKENS.map((t) => <TokenRow key={t.hex} token={t} dark />)}
          </div>
        </section>

        <section className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
          <h3 className="text-heading text-lg font-semibold mb-2">Usage rules</h3>
          <ul className="text-sm text-neutral-700 space-y-1.5 list-disc pl-5">
            <li><strong>Body</strong> and <strong>headline</strong> resolve automatically — you don't need to add a class in most cases.</li>
            <li>Use <code>.text-secondary</code> for descriptions, subtitles, breadcrumbs, meta lines under a headline.</li>
            <li>Use <code>.text-helper</code> for tiny hint text (timestamps, form-field hints, metadata).</li>
            <li>Use <code>.text-disabled</code> only for genuinely disabled elements. Never for de-emphasis.</li>
            <li>On dark surfaces, wrap the container in <code>.on-dark</code> or <code>[data-surface="dark"]</code>. Anything inside <code>&lt;aside&gt;</code> gets the dark palette automatically.</li>
          </ul>
        </section>
      </div>
    </AppShell>
  );
}
